import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import RTPageLayout from '../common/RTPageLayout';
import { toastSuccess, toastError } from '../common/toast';
import { apiGet, apiPost } from '../../api/apiClient';
import tenantStorage from '../../utils/tenantStorage';
import {
  USERNAME_RULES,
  USERNAME_RULES_MESSAGE,
  validateManagementUsername,
} from '../../utils/authHelpers';
import '../common/rtCommon.css';
import './ChangeUsernameForm.css';

const ChangeUsernameForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUsername, setCurrentUsername] = useState(
    () => tenantStorage.getItem('username') || '',
  );
  const [newUsername, setNewUsername] = useState('');
  const [availability, setAvailability] = useState(null); // null | true | false
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState('');

  useEffect(() => {
    const accessToken = tenantStorage.getItem('accessToken');
    if (!accessToken) {
      navigate('/login', { state: { mode: 'SESSION_EXPIRED' } });
    }
  }, [navigate]);

  const resetAvailability = () => {
    setAvailability(null);
    setFieldError('');
  };

  const handleUsernameChange = (value) => {
    setNewUsername(value.toLowerCase());
    resetAvailability();
  };

  const handleCheckAvailability = async () => {
    const validationError = validateManagementUsername(newUsername);
    if (validationError) {
      setFieldError(validationError);
      setAvailability(null);
      return;
    }

    const trimmed = newUsername.trim().toLowerCase();
    if (trimmed === (currentUsername || '').trim().toLowerCase()) {
      setFieldError('New username is the same as your current username');
      setAvailability(null);
      return;
    }

    setChecking(true);
    setFieldError('');
    try {
      const res = await apiGet(
        `/auth/native/check-username?username=${encodeURIComponent(trimmed)}`,
      );
      const available = Boolean(res.data?.available);
      setAvailability(available);
      if (!available) {
        setFieldError('Username is already taken');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : detail?.detail || 'Failed to check username availability';
      setFieldError(msg);
      setAvailability(null);
      toastError(msg);
    } finally {
      setChecking(false);
    }
  };

  const canSave =
    availability === true &&
    !validateManagementUsername(newUsername) &&
    newUsername.trim().toLowerCase() !==
      (currentUsername || '').trim().toLowerCase();

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canSave) {
      toastError(
        'Please check that the new username is available before saving',
      );
      return;
    }

    setSaving(true);
    try {
      const res = await apiPost('/auth/native/change-username', {
        new_username: newUsername.trim().toLowerCase(),
      });
      const nextUsername =
        res.data?.username || newUsername.trim().toLowerCase();
      if (res.data?.access_token) {
        tenantStorage.setItem('accessToken', res.data.access_token);
      }
      if (res.data?.refresh_token) {
        tenantStorage.setItem('refreshToken', res.data.refresh_token);
      }
      tenantStorage.setItem('username', nextUsername);
      setCurrentUsername(nextUsername);
      setNewUsername('');
      setAvailability(null);
      toastSuccess(res.data?.message || 'Username changed successfully');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : detail?.detail || 'Failed to change username';
      setFieldError(msg);
      setAvailability(null);
      toastError(msg);
    } finally {
      setSaving(false);
    }
  };

  const backButton = (
    <RTButton type="button" variant="secondary" onClick={() => navigate(-1)}>
      &#8592; Back
    </RTButton>
  );

  return (
    <RTPageLayout
      title="Change Username"
      actions={backButton}
      mainSection
      activePath={location.pathname}
    >
      <div className="change-username-panel">
        <Form onSubmit={handleSave}>
          <Form.Group controlId="currentUsername" className="form-group">
            <Form.Label>Current Username</Form.Label>
            <Form.Control
              type="text"
              value={currentUsername}
              disabled
              readOnly
            />
          </Form.Group>

          <Form.Group controlId="newUsername" className="form-group">
            <Form.Label>New Username</Form.Label>
            <div className="change-username-input-row">
              <Form.Control
                type="text"
                value={newUsername}
                onChange={(e) => handleUsernameChange(e.target.value)}
                placeholder="Enter new username"
                maxLength={64}
                autoComplete="username"
                disabled={saving}
                isInvalid={Boolean(fieldError)}
              />
              <RTButton
                type="button"
                variant="secondary"
                disabled={saving || checking || !newUsername.trim()}
                onClick={handleCheckAvailability}
              >
                {checking ? (
                  <>
                    <RTSpinner size="small" />
                    &nbsp;Checking…
                  </>
                ) : (
                  'Check availability'
                )}
              </RTButton>
            </div>
            {fieldError ? (
              <div className="change-username-error">{fieldError}</div>
            ) : availability === true ? (
              <div className="change-username-ok">Username is available</div>
            ) : (
              <div className="change-username-hint">
                {USERNAME_RULES_MESSAGE}
              </div>
            )}
          </Form.Group>

          <div className="change-username-rules">
            <strong>Username rules</strong>
            <ul>
              {USERNAME_RULES.map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
          </div>

          <div className="form-buttons">
            <RTButton
              type="button"
              variant="secondary"
              disabled={saving}
              onClick={() => navigate(-1)}
            >
              Cancel
            </RTButton>
            <RTButton type="submit" disabled={saving || !canSave}>
              {saving ? (
                <>
                  <RTSpinner size="small" />
                  &nbsp;Saving…
                </>
              ) : (
                'Save Username'
              )}
            </RTButton>
          </div>
        </Form>
      </div>
      {(saving || checking) && <RTSpinner fullScreen={true} />}
    </RTPageLayout>
  );
};

export default ChangeUsernameForm;
